import { memo } from 'react';
import { Handle, Position } from 'reactflow';
import { getStanceToken } from '../chatDagTokens';
import { usePersonaName } from '../../../../hooks/usePersonaColor';
import type { SquadMergeData } from '../types';

function CitationPill({ personaId, stance }: { personaId: string; stance?: string }) {
  const name = usePersonaName(personaId);
  const st = getStanceToken(stance);
  return (
    <span className="inline-flex items-center gap-0.5 rounded-full bg-indigo-100 px-1.5 py-0.5 text-[11px] text-indigo-700 dark:bg-indigo-900/60 dark:text-indigo-300">
      <span aria-hidden>{st.glyph}</span>
      <span className="truncate max-w-[80px]">{name}</span>
    </span>
  );
}

function SquadMergeNodeImpl({ data }: { data: SquadMergeData }) {
  const citations = data.citations ?? [];
  const failedCount = data.failedCount ?? 0;

  return (
    <div
      className="rounded-lg border-2 border-indigo-400 bg-indigo-50 px-3 py-2 shadow-sm dark:border-indigo-500 dark:bg-indigo-950/40"
      style={{ width: 280 }}
    >
      <Handle type="target" id="t" position={Position.Top} style={{ opacity: 0 }} />
      <div className="flex items-center gap-1.5 text-xs font-semibold text-indigo-700 dark:text-indigo-300">
        <span aria-hidden>◆</span>
        <span>Squad synthesis</span>
        <span className="ml-auto font-normal text-indigo-500 dark:text-indigo-400">
          {data.reportedCount}/{data.memberCount}
        </span>
      </div>
      <div className="mt-0.5 text-[10px] text-indigo-600/70 dark:text-indigo-400/70">
        {failedCount > 0
          ? `synth from ${data.reportedCount} of ${data.memberCount}`
          : `synth from ${data.memberCount}`}
        {failedCount > 0 && (
          <span className="ml-1 text-rose-500 dark:text-rose-400">
            · {failedCount} failed
          </span>
        )}
      </div>
      <div className="mt-1 line-clamp-3 text-[11px] leading-snug text-gray-700 dark:text-gray-200">
        {data.synthPreview}
      </div>
      {citations.length > 0 && (
        <div className="mt-1.5 flex flex-wrap gap-1">
          {citations.map((c) => (
            <CitationPill
              key={c.spineRootMessageId}
              personaId={c.personaId}
              stance={c.stance}
            />
          ))}
        </div>
      )}
      <Handle type="source" id="b" position={Position.Bottom} style={{ opacity: 0 }} />
    </div>
  );
}

export default memo(SquadMergeNodeImpl);
